import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, Download, Loader2, Inbox, Mail, RefreshCw, CheckCircle2, Circle,
} from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aiga_admin_token";

const fieldBase =
  "w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3.5 text-white placeholder:text-slatemuted focus:border-teal transition-colors";

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const LoginView = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Signed in.");
      onSuccess(data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-3xl glass p-8" data-testid="admin-login-card">
        <div className="flex items-center gap-3">
          <Logo imgClass="h-8" />
          <span className="font-display font-bold text-white">Submissions Inbox</span>
        </div>
        <h1 className="mt-8 font-display text-2xl font-bold text-white flex items-center gap-2">
          <Lock className="h-5 w-5 text-teal" aria-hidden="true" /> Admin sign in
        </h1>
        <p className="mt-2 text-sm text-slatemuted">Private access for Aiga Enable.</p>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-slate-200 mb-2">Email</label>
            <input
              id="admin-email" type="email" autoComplete="email" value={email}
              onChange={(e) => setEmail(e.target.value)} className={fieldBase}
              placeholder="you@aigaenable.co.nz" required data-testid="admin-email-input"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-slate-200 mb-2">Password</label>
            <input
              id="admin-password" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} className={fieldBase}
              placeholder="••••••••" required data-testid="admin-password-input"
            />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit" disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-6 py-3.5 text-base font-semibold text-navy-900 hover:bg-cyan transition-colors disabled:opacity-70"
            data-testid="admin-login-submit"
          >
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Signing in…</> : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/submissions`, authHeader());
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/submissions/export`, { ...authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `aiga-interest-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const optInCount = rows.filter((r) => r.updates_opt_in).length;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo imgClass="h-7" />
            <span className="hidden sm:block font-display font-bold text-white text-sm">Submissions Inbox</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-sm text-slatemuted mr-2">{user?.email}</span>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-white flex items-center gap-3">
              <Inbox className="h-7 w-7 text-teal" aria-hidden="true" /> Registered interest
            </h1>
            <p className="mt-2 text-slatemuted">
              {rows.length} {rows.length === 1 ? "submission" : "submissions"} · {optInCount} opted in for updates
            </p>
          </div>
          <button
            onClick={exportCsv}
            disabled={exporting || rows.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-teal px-6 py-3 text-sm font-semibold text-navy-900 hover:bg-cyan transition-colors disabled:opacity-60"
            data-testid="admin-export-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" aria-hidden="true" />}
            Export CSV
          </button>
        </div>

        <div className="mt-8 rounded-2xl glass overflow-hidden" data-testid="admin-submissions-table">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slatemuted">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Inbox className="h-10 w-10 text-slatemuted" aria-hidden="true" />
              <p className="mt-3 text-slate-300">No submissions yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs font-mono uppercase tracking-wider text-slatemuted border-b border-white/10">
                  <tr>
                    <th className="px-5 py-4 font-medium">Name</th>
                    <th className="px-5 py-4 font-medium">Email</th>
                    <th className="px-5 py-4 font-medium">Role</th>
                    <th className="px-5 py-4 font-medium">Message</th>
                    <th className="px-5 py-4 font-medium">Updates</th>
                    <th className="px-5 py-4 font-medium">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`admin-row-${i}`}>
                      <td className="px-5 py-4 text-white font-medium whitespace-nowrap">{r.name}</td>
                      <td className="px-5 py-4">
                        <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 text-teal hover:text-cyan">
                          <Mail className="h-3.5 w-3.5" aria-hidden="true" /> {r.email}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-slate-300 whitespace-nowrap">{r.role || "—"}</td>
                      <td className="px-5 py-4 text-slate-300 max-w-xs truncate" title={r.message || ""}>{r.message || "—"}</td>
                      <td className="px-5 py-4">
                        {r.updates_opt_in ? (
                          <span className="inline-flex items-center gap-1.5 text-teal"><CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Yes</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-slatemuted"><Circle className="h-4 w-4" aria-hidden="true" /> No</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-slatemuted whitespace-nowrap">{formatDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export const AdminInbox = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    axios
      .get(`${API}/auth/me`, authHeader())
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slatemuted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginView onSuccess={setUser} />;
};

export default AdminInbox;
